import { globals } from 'config/globals';
import { device } from 'utils/device';
import HomeColours from 'app/views/home/config/HomeColours';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PHYSICS,
	BEHAVIOUR_PROGRESS_PARALLAX,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	PHYS_BOUNDARY,
	PHYS_RAIN,
	PHYS_Y_BOUNCE,
	REPELL_VECTOR,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { FRAME_RING, FRAME_SOLID2 } from 'app/views/home/webgl/particlesOptimised/core/frames';
import { GROUP_SCREENS } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensContext';

import type { ScreensContext } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * DropMachinePhase — the reference ScreensDepartment's 'dropMachine' section
 * (_doDropMachine + _doDrop + ScreensParticleProcessor.leftDrop), phase-file'd
 * in the screens retrofit. BODIES are the verbatim stage-5 lane ports that
 * lived in particlesOptimised ScreensDepartment._doDropMachine/_doDrop/
 * _leftDrop — the retrofit moved them, it did not change them.
 *
 * VISUAL: breathing dots scatter across the TVs, then drop one by one on
 * randomised 0-3s timers under gravity, bouncing off the systems-block hit
 * shapes (ParticleHitTests pixel readback in the physics kernel) while the
 * TVs flicker on and the drop-machine block sprites fade in, everything
 * shutting down over the last two seconds.
 *
 * The per-lane drop timers are ScriptList entries in GROUP_SCREENS —
 * cancelled on scene stop/park only, never at phase transitions (all fire
 * within the phase's own 8.8s in the reference). hide()/reset() own nothing:
 * pending scripts and combo entries carry over exactly like the reference
 * (the swirl deliberately stacks on this phase's leftover combo entries).
 */
export class DropMachinePhase implements DepartmentPhase {
	public readonly name = 'dropMachine';

	private _ctx: ScreensContext;

	constructor(ctx: ScreensContext) {
		this._ctx = ctx;
	}

	public init(): void {
		//No one-time setup — the phase works straight off the shared lanes.
	}

	/** _doDropMachine, verbatim. */
	public start(): void {
		const ctx = this._ctx;

		let totalParticles = HomeFieldConfig.screensDropMachineParticles;
		ParticleHitTests.active = true;

		if (device.innerWidth < 700) {
			totalParticles *= HomeFieldConfig.screensDropMachineParticlesMobileScale;
		}

		this._doDrop(totalParticles);
		HomeScreens.showDropMachineBlocks();

		ctx.counters.clear();

		HomeScreens.allOff();

		const showBottomRightDesktop = 2000;
		const showBottomLeft = 1800;
		const hideTopLeftDesktop = 7000;
		const hideTopRightDesktop = 6800;

		ctx.counters.add(() => {
			HomeScreens.on('topRight');
		}, 500);

		ctx.counters.add(() => {
			HomeScreens.on('topLeft');
		}, 400);

		ctx.counters.add(() => {
			HomeScreens.on('bottomLeft');
		}, showBottomLeft);

		ctx.counters.add(() => {
			HomeScreens.on('bottomRight');
		}, showBottomRightDesktop);

		ctx.counters.add(() => HomeScreens.hideDropMachineBlocks(), 6000);

		ctx.counters.add(() => {
			HomeScreens.off('topRight');
		}, hideTopRightDesktop);

		ctx.counters.add(() => {
			HomeScreens.off('topLeft');
		}, hideTopLeftDesktop);

		ctx.counters.add(() => HomeScreens.allOff(), 8200);
	}

	public tick(): void {
		//All motion is kernel-side (physics gravity + hit-test bounces +
		//VECTOR repell); the TV/block beats run on the shared ComboCounter.
	}

	public hide(): void {
		//Pending drop scripts / combo entries carry over by design (see the
		//class header); ParticleHitTests.active goes false in the department's
		//onTransition preamble (reference _nextScreensSection). Safe on a
		//never-started phase.
	}

	public reset(): void {
		//No persistent phase state.
	}

	/** _doDrop, verbatim. */
	private _doDrop(totalParticles: number): void {
		if (globals.colourPhase === 'yellow') globals.bounceColourTarget = HomeColours.dynamicSets.m4g;
		else globals.bounceColourTarget = HomeColours.purpleBlue;

		const ctx = this._ctx;
		const core = ctx.core;
		let i = 0;
		const mobileLayout = device.innerWidth <= HomeScreens.switchScreensLayoutAt;

		for (let n = 0; n < ctx.range.count; n++) {
			const lane = ctx.range.start + n;
			i++;

			//Reference increments BEFORE the compare — an off-by-one that
			//drops one particle (totalParticles - 1 actually drop). Kept:
			if (i < totalParticles) {
				this._leftDrop(lane);
				core.blendAdd[lane] = 0; //sprite.blendMode = null

				core.textureId[lane] = Math.random() < 0.2 ? FRAME_RING : FRAME_SOLID2;

				let baseScale: number;
				const baseSeedBig = mobileLayout ? 0.6 : 0.8;
				const baseSeedSmall = mobileLayout ? 0.38 : 0.5;
				if (Math.random() < 0.2) {
					baseScale = 0.1 + Math.random() * baseSeedBig;
				} else {
					baseScale = 0.1 + Math.random() * baseSeedSmall;
				}

				//hardSetScale(baseScale):
				core.scale[lane] = baseScale;
				core.tScale[lane] = baseScale;
				core.tScaleSaved[lane] = baseScale;

				//startBreathing(baseScale, rand*0.02, rand*0.4):
				core.breathBase[lane] = baseScale;
				core.breathChange[lane] = Math.random() * 0.02;
				core.breathMax[lane] = Math.random() * 0.4;
				core.breathAdd[lane] = 0;
				core.breathDir[lane] = 1;
				core.behaviourMask[lane] |= BEHAVIOUR_BREATHING;

				const x = -0.08 + Math.random() * 0.15;
				const y = mobileLayout ? Math.random() * -0.15 : Math.random() * 0.15;

				core.tAlpha[lane] = 0.6 + Math.random() * 0.4;

				//setVectorStyleMovement(false):
				writeVectorConfig(core, lane, VEC_X, false);
				writeVectorConfig(core, lane, VEC_Y, false);

				//position.set({x, y}, true):
				ctx.setHardPosition(lane, x, y);
			} else {
				//deactivate():
				core.stateFlags[lane] &= ~FLAG_ACTIVE;
			}
		}
	}

	/** ScreensParticleProcessor.leftDrop: the drop is armed on a randomised
	 *  counter (0-3000ms), then physics takes over with a VECTOR repell. */
	private _leftDrop(lane: number): void {
		this._ctx.deps.scripts.addMs(
			lane,
			Math.random() * 3000,
			(l: number) => {
				const core = this._ctx.core;

				//deactivateAllBehaviours(true):
				core.behaviourMask[l] =
					(core.behaviourMask[l] & (BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X)) |
					BEHAVIOUR_PROGRESS_PARALLAX;

				//particlePhysics.active = true + leftDrop() [type 'screens':
				//no boundary processing, no y bounce]:
				core.behaviourMask[l] |= BEHAVIOUR_PHYSICS;
				core.physFlags[l] &= ~(PHYS_BOUNDARY | PHYS_Y_BOUNCE | PHYS_RAIN);
				const m = Math.random() < 0.5 ? -1 : 1;
				core.vx[l] = (0.0001 + Math.random() * 0.0003) * m;
				core.vy[l] = 0;
				core.gravity[l] = 0.000063 + Math.random() * 0.000006;

				//repell: VECTOR, radius 100, randomisePower(0.00005, 0.0000001):
				core.behaviourMask[l] |= BEHAVIOUR_REPELL;
				core.repellType[l] = REPELL_VECTOR;
				core.repellRadius[l] = 100;
				core.repellPower[l] = 0.00005 + Math.random() * 0.0000001;

				//setEase({scale: 4}):
				core.easeScale[l] = 4;
			},
			false,
			GROUP_SCREENS
		);
	}
}
